import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  AiOutlineStar,
  AiOutlineCheckCircle,
  AiOutlineDeploymentUnit,
} from 'react-icons/ai';

import img from '../images/Sabean/sabean about 2.jpg';
import img2 from '../images/Sabean/sabean about.jpg';
import img3 from '../images/Sabean/SABEAN Water Meter-2819.jpg';
import qualityPolicyImage from '../images/Sabean/quality.png';
import CounterUp from '../Components/counterUp';

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: { opacity: 1, y: 0 },
};

const coreValues = [
  'Quality and Reliability',
  'Innovation and Technology',
  'Local Commitment',
  'Customer Focus',
  'Integrity and Accountability',
  'Sustainability and Responsibility',
  'Excellence in Manufacturing',
  'Global Competitiveness',
];

const SectionTitle = ({ label, title, desc }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.25 }}
    transition={{ duration: 0.7 }}
    className="text-center max-w-4xl mx-auto mb-12"
  >
    <p className="uppercase tracking-[0.28em] text-sm font-semibold text-[#0077b6] mb-3">
      {label}
    </p>

    <h2 className="text-4xl md:text-5xl font-bold text-[#03045e] mb-4">
      {title}
    </h2>

    {desc && <p className="text-gray-600 text-lg leading-relaxed">{desc}</p>}
  </motion.div>
);

const About = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="mt-28 bg-sky-50">
      <section className="font-serif md:mx-12 mx-4 pt-4 pb-16">
        {/* Hero Section */}
        <section className="relative overflow-hidden rounded-3xl bg-[#03045e] text-white mb-16 shadow-xl">
          <div className="absolute inset-0 bg-gradient-to-r from-[#03045e] via-[#0077b6]/80 to-black/20 z-10"></div>

          <img
            src={img2}
            alt="SABEAN factory"
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />

          <motion.div
            initial={{ opacity: 0, y: 45 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="relative z-20 max-w-4xl px-6 md:px-16 py-20 md:py-28"
          >
            <p className="uppercase tracking-[0.3em] text-sm text-sky-200 mb-4">
              About SABEAN
            </p>

            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
              Building Ethiopia’s Future in Water Meter Manufacturing
            </h1>

            <p className="text-lg md:text-xl leading-relaxed text-gray-100">
              SABEAN Base Metal Engineering PLC is committed to manufacturing
              reliable mechanical and modern smart water meters that support
              efficient water management, local production, and sustainable
              development.
            </p>
          </motion.div>
        </section>

        {/* Welcome / Company Overview */}
        <section className="grid lg:grid-cols-2 gap-8 items-center mb-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7 }}
            className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-sky-100"
          >
            <p className="uppercase tracking-[0.25em] text-sm font-semibold text-[#0077b6] mb-3">
              Welcome
            </p>

            <h2 className="text-4xl font-bold text-[#03045e] mb-6">
              Welcome To SABEAN
            </h2>

            <p className="text-gray-700 text-lg leading-relaxed mb-5">
              SABEAN is more than a water meter manufacturer. It is a company
              working toward better water measurement, fair billing, reduced
              water loss, and stronger local manufacturing capacity.
            </p>

            <p className="text-gray-700 text-lg leading-relaxed">
              By producing accurate and durable water meters locally, SABEAN
              contributes to Ethiopia’s water sector, supports import
              substitution, and helps build a more sustainable water management
              system.
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative overflow-hidden rounded-3xl shadow-xl"
          >
            <img
              src={img3}
              alt="SABEAN water meter"
              className="w-full h-[460px] object-cover"
            />

            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
              <h3 className="text-2xl font-bold">Accurate. Reliable. Local.</h3>
              <p className="text-sm mt-2">
                Supporting modern water management through quality production.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Quality Policy Image */}
        <section className="mb-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7 }}
            className="bg-white rounded-3xl p-6 md:p-10 shadow-lg border border-sky-100"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#03045e] text-center mb-8">
              Our Quality Policy
            </h2>

            <div className="max-w-5xl mx-auto bg-sky-50 rounded-3xl p-4 md:p-6 border border-sky-100">
              <button
                type="button"
                onClick={() =>
                  setSelectedImage({
                    src: qualityPolicyImage,
                    alt: 'Our Quality Policy',
                  })
                }
                className="w-full cursor-zoom-in group"
                aria-label="Open Quality Policy image"
              >
                <img
                  src={qualityPolicyImage}
                  alt="Our Quality Policy"
                  className="w-full h-auto rounded-2xl object-contain transition duration-300 group-hover:brightness-90"
                />
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
                Click the image to view full size
              </p>
            </div>
          </motion.div>
        </section>

        {/* Vision and Mission */}
        <section className="mb-20">
          <SectionTitle
            label="Direction"
            title="Vision and Mission"
            desc="Our direction is built on local commitment, quality production, innovation, and global competitiveness."
          />

          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.7 }}
              className="relative overflow-hidden bg-[#0077b6] text-white p-8 md:p-10 rounded-3xl shadow-xl"
            >
              <div className="absolute -right-12 -top-12 w-40 h-40 bg-white/10 rounded-full"></div>
              <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-white/10 rounded-full"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center text-4xl mb-6">
                  <AiOutlineStar />
                </div>

                <h3 className="text-3xl font-bold mb-5">Our Vision</h3>

                <p className="text-lg leading-relaxed text-sky-50">
                  To become a leading manufacturer of mechanical and modern
                  smart water meters in Africa by fully satisfying domestic
                  demand, as well as providing innovative and high-quality meter
                  solutions to domestic, regional, and international markets.
                </p>
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative overflow-hidden bg-white text-gray-800 p-8 md:p-10 rounded-3xl shadow-xl border border-sky-100"
            >
              <div className="absolute -right-12 -top-12 w-40 h-40 bg-sky-100 rounded-full"></div>
              <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-sky-50 rounded-full"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-sky-50 text-[#0077b6] flex items-center justify-center text-4xl mb-6">
                  <AiOutlineCheckCircle />
                </div>

                <h3 className="text-3xl font-bold text-[#03045e] mb-5">
                  Our Mission
                </h3>

                <p className="text-lg leading-relaxed text-gray-700">
                  To manufacture and supply quality-assured mechanical and
                  modern smart water meters that fully meet domestic market
                  needs, while establishing a competitive presence in foreign
                  countries and international export markets.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Core Values Together */}
        <section className="mb-20">
          <SectionTitle
            label="Culture"
            title="Core Values of SABEAN"
            desc="Our core values express the principles that guide SABEAN’s production, service, relationships, and growth."
          />

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7 }}
            className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-sky-100"
          >
            <div className="flex flex-wrap justify-center gap-3">
              {coreValues.map((value, index) => (
                <span
                  key={index}
                  className="px-5 py-3 rounded-full bg-sky-50 text-[#0077b6] font-semibold border border-sky-100 hover:bg-[#0077b6] hover:text-white transition duration-300"
                >
                  {value}
                </span>
              ))}
            </div>

            <p className="text-center text-gray-600 text-lg leading-relaxed mt-8 max-w-4xl mx-auto">
              Together, these values reflect SABEAN’s commitment to quality,
              innovation, homegrown production, customer satisfaction,
              integrity, sustainability, manufacturing excellence, and global
              competitiveness.
            </p>
          </motion.div>
        </section>

        {/* Strategic Plan Together */}
        <section className="mb-20">
          <SectionTitle
            label="Growth Plan"
            title="Strategic Plan"
            desc="A clear direction for production growth, market expansion, innovation, and long-term excellence."
          />

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden bg-[#03045e] text-white rounded-3xl p-8 md:p-12 shadow-xl"
          >
            <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-white/10"></div>
            <div className="absolute -left-16 -bottom-16 w-48 h-48 rounded-full bg-[#0077b6]/30"></div>

            <div className="relative z-10 max-w-5xl mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center text-4xl mb-6">
                <AiOutlineDeploymentUnit />
              </div>

              <p className="text-lg md:text-xl leading-relaxed text-gray-100">
                SABEAN’s strategic plan focuses on fully satisfying domestic
                demand for water meters while fostering innovation in mechanical
                and modern meter solutions. The company aims to enhance
                manufacturing capacity and productivity, ensure regional and
                international export growth, support sustainable water
                management, build technology transfer and domestic knowledge
                capacity, strengthen relationships with customers and
                stakeholders, and maintain operational excellence with high
                quality.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Achievements */}
        <section className="mb-20 bg-white rounded-3xl p-6 md:p-10 shadow-lg border border-sky-100">
          <div className="flex gap-8 md:flex-row flex-col items-center">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.7 }}
              className="md:w-1/3"
            >
              <h3 className="text-4xl md:text-5xl text-[#0077b6] font-bold">
                Our Achievements
              </h3>

              <p className="font-serif text-lg text-[#333333] text-justify my-6">
                Since our establishment, SABEAN has produced over 500,000 water
                meters and aims to double its production capacity to meet the
                growing demand. We supply Ethiopian water supply authorities and
                are expanding into the Horn of Africa region.
              </p>
            </motion.div>

            <div className="md:w-2/3">
              <CounterUp />
            </div>
          </div>
        </section>

        {/* CEO Message */}
        <section className="grid lg:grid-cols-2 gap-0 overflow-hidden rounded-3xl bg-[#03045e] text-white shadow-xl mb-10">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="p-8 md:p-12"
          >
            <p className="uppercase tracking-[0.28em] text-sm text-sky-200 mb-4">
              Leadership Message
            </p>

            <h2 className="text-4xl font-bold mb-6">Message from the CEO</h2>

            <p className="text-lg leading-relaxed mb-5 text-gray-100">
              As one of the founders and CEO of Sabean Base Metal Engineering, I
              am delighted to share our vision for a water-secure future in
              Ethiopia and beyond.
            </p>

            <p className="text-lg leading-relaxed mb-5 text-gray-100">
              Our journey began with a commitment to address Ethiopia&apos;s
              unique water management challenges through domestic water meter
              manufacturing. By sourcing 80% of our raw materials locally, and
              manufacturing major components of the meter in our factory, we
              successfully reduced reliance on substandard imported water
              meters. Ethiopia has always been relying on imported water meters
              and has often proved costly, unsustainable and unreliable.
            </p>

            <p className="text-lg leading-relaxed mb-5 text-gray-100">
              Our MultiJet water meters are more than just tools for measuring
              water consumption; they are catalysts for positive change! In
              addition to the transfer of this technology, generating foreign
              currency and creating job opportunities for young citizens,
              manufacturing high accuracy water meters play a crucial role in
              preserving water resources while serving as precise instruments
              for revenue collection, contributing to the sustainable
              management of Ethiopia’s clean water supply.
            </p>

            <p className="text-lg leading-relaxed mb-5 text-gray-100">
              As Ethiopia&apos;s urbanization accelerates, the importance of
              efficient water metering grows. We are ISO9001:2015 certified and
              our high-quality water meters, designed to meet rigorous ISO
              4064-2005 standards, are essential for both water conservation and
              revenue generation. During the past couple of years, the trust
              that we have established with consumers and suppliers is a
              testament to our dedication to quality and reliability.
            </p>

            <p className="text-lg leading-relaxed mb-5 text-gray-100">
              Our impact extends beyond Ethiopia, contributing to regional and
              global water security. We recognize that effective water
              management requires collaboration, and we are open to partnerships
              that align with our mission. Whether through transfer of
              technology, creation of job opportunities, import substitution, or
              environmental stewardship, we are eager to work with like-minded
              organizations and individuals to make a difference.
            </p>

            <p className="text-lg leading-relaxed mb-5 text-gray-100">
              Together, we can create a sustainable, water-secure future. I
              invite you to join us on this journey as we continue to innovate,
              inspire, and impact lives.
            </p>

            <p className="text-lg leading-relaxed font-semibold">
              We Value Every Droplet of Water!
            </p>

            <p className="text-lg leading-relaxed mt-4 text-sky-100">
              Ainishet Asres (Ph.D.)
              <br />
              CEO, Sabean Base Metal Engineering PLC
              <br />
              Addis Ababa, Ethiopia
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="h-full"
          >
            <img
              src={img}
              alt="SABEAN CEO message"
              className="w-full h-full min-h-[420px] object-cover"
            />
          </motion.div>
        </section>
      </section>

      {/* Full Image Preview */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/85 z-[9999] flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-5xl w-full max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 bg-white text-[#03045e] px-4 py-2 rounded-lg font-semibold hover:bg-gray-200 transition"
            >
              Close
            </button>

            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="w-full max-h-[90vh] object-contain rounded-xl bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default About;